/**
 * LocationVerifyStep — Scheduling Wizard pre-flight (Phase 41-02 / Issue #65).
 *
 * Sits at the top of ScheduleModal. Gates the rest of the form (date/manager
 * fields disabled) until the planner verifies the site_address via Google
 * Geocoding API + picks the correct match.
 *
 * State machine:
 *   editing   → planner edits the address text + clicks Verify
 *   matches   → top-3 Google matches shown as radios; planner picks one
 *   confirming → spinner while POST /confirm-location runs
 *   verified  → green pill + onVerified(formattedAddress) fired (one-shot)
 *   error     → red error text + Edit link to re-enter
 *
 * Already-verified work_items (Q3 skip) mount in `verified` mode directly:
 *   - parent passes initiallyVerified=true
 *   - useEffect fires onVerified(initialAddress) once on mount
 *
 * HubSpot sync status is NOT shown to the planner (Q1 silent — audit log
 * records the outcome for backend recovery).
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, Loader2, MapPin } from 'lucide-react';
import { api } from '../../lib/api';
import type { ApiGeocodeMatch } from '../../lib/api';

type Mode = 'editing' | 'matches' | 'confirming' | 'verified' | 'error';

interface LocationVerifyStepProps {
  workItemId: string;
  initialAddress: string;
  initiallyVerified: boolean;
  onVerified: (formattedAddress: string) => void;
}

export function LocationVerifyStep({
  workItemId,
  initialAddress,
  initiallyVerified,
  onVerified,
}: LocationVerifyStepProps) {
  const [mode, setMode] = useState<Mode>(initiallyVerified ? 'verified' : 'editing');
  const [addressDraft, setAddressDraft] = useState(initialAddress);
  const [matches, setMatches] = useState<ApiGeocodeMatch[]>([]);
  const [pickedIdx, setPickedIdx] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [verifiedAddress, setVerifiedAddress] = useState<string>(initialAddress);

  // One-shot onVerified for initiallyVerified case + post-confirm. Avoids
  // re-firing on every render when the parent re-renders due to its own state.
  const firedRef = useRef(false);
  useEffect(() => {
    if (mode === 'verified' && !firedRef.current) {
      firedRef.current = true;
      onVerified(verifiedAddress);
    }
  }, [mode, verifiedAddress, onVerified]);

  const verifyMutation = useMutation({
    mutationFn: (addr: string) => api.workItems.verifyLocation(workItemId, addr),
    onSuccess: (data) => {
      setMatches(data.matches);
      setPickedIdx(null);
      setErrorMsg(null);
      setMode('matches');
    },
    onError: () => {
      setErrorMsg('Could not verify — please try again.');
      setMode('error');
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (picked: ApiGeocodeMatch) => api.workItems.confirmLocation(workItemId, picked),
    onSuccess: (data) => {
      setVerifiedAddress(data.formatted_address);
      setMode('verified');
    },
    onError: () => {
      setErrorMsg('Could not save the verified address — please try again.');
      setMode('error');
    },
  });

  function handleVerifyClick() {
    if (!addressDraft.trim()) return;
    setMode('confirming'); // brief loading state before mutation settles
    verifyMutation.mutate(addressDraft.trim(), {
      onError: (err) => {
        // 422 carries the geocode error code (e.g. zero_results) in
        // response body. apiFetch throws on non-2xx; surface a readable
        // message based on whether it was a known zero-result-style error.
        const msg = err instanceof Error ? err.message : String(err);
        if (/zero_results/i.test(msg)) {
          setErrorMsg(
            'Google could not find this address. Try adding more detail (postcode, town, street).',
          );
        } else if (/no_api_key|request_denied/i.test(msg)) {
          setErrorMsg('Address verification is currently unavailable. Contact admin.');
        } else {
          setErrorMsg('Could not verify — please try again.');
        }
        setMode('error');
      },
    });
  }

  function handleConfirmClick() {
    if (pickedIdx === null) return;
    const picked = matches[pickedIdx];
    setMode('confirming');
    confirmMutation.mutate(picked);
  }

  function handleEditClick() {
    setErrorMsg(null);
    setMode('editing');
  }

  // ─── verified: green pill ───────────────────────────────────────────────
  if (mode === 'verified') {
    return (
      <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md px-3 py-2 text-[12px]">
        <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-medium">Address verified</div>
          <div className="text-emerald-800 break-words">{verifiedAddress}</div>
        </div>
      </div>
    );
  }

  // ─── confirming: spinner ────────────────────────────────────────────────
  if (mode === 'confirming') {
    return (
      <div className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-md px-3 py-3 text-[12px] text-neutral-600">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>Verifying address…</span>
      </div>
    );
  }

  // ─── matches: top-3 picker ──────────────────────────────────────────────
  if (mode === 'matches') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-3 text-[12px] space-y-2">
        <div className="flex items-center gap-2 text-amber-900 font-medium">
          <MapPin className="w-3.5 h-3.5" />
          <span>Pick the correct address</span>
        </div>
        <div className="space-y-1.5">
          {matches.map((m, idx) => (
            <label
              key={m.placeId}
              className="flex items-start gap-2 cursor-pointer p-1.5 rounded hover:bg-amber-100 transition-colors"
            >
              <input
                type="radio"
                name="geocode-match"
                checked={pickedIdx === idx}
                onChange={() => setPickedIdx(idx)}
                className="mt-0.5"
              />
              <span className="text-neutral-800 break-words">{m.formattedAddress}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={handleEditClick}
            className="text-[11px] text-neutral-500 hover:text-neutral-700 underline-offset-2 hover:underline"
          >
            Edit address
          </button>
          <button
            type="button"
            onClick={handleConfirmClick}
            disabled={pickedIdx === null}
            className="px-3 py-1.5 text-[12px] bg-[#F26522] text-white rounded-md hover:bg-[#d9561d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Confirm address
          </button>
        </div>
      </div>
    );
  }

  // ─── error: red text + Edit link ────────────────────────────────────────
  if (mode === 'error') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md px-3 py-3 text-[12px] space-y-2">
        <div className="text-red-800">{errorMsg ?? 'Could not verify.'}</div>
        <input
          type="text"
          value={addressDraft}
          onChange={(e) => setAddressDraft(e.target.value)}
          placeholder="Enter site address"
          className="w-full text-[12px] border border-neutral-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522]"
        />
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleVerifyClick}
            disabled={!addressDraft.trim()}
            className="px-3 py-1.5 text-[12px] bg-[#F26522] text-white rounded-md hover:bg-[#d9561d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Verify
          </button>
        </div>
      </div>
    );
  }

  // ─── editing: address input + Verify button (default) ───────────────────
  return (
    <div className="bg-neutral-50 border border-neutral-200 rounded-md px-3 py-3 text-[12px] space-y-2">
      <div className="flex items-center gap-2 text-neutral-700 font-medium">
        <MapPin className="w-3.5 h-3.5" />
        <span>Verify site address before scheduling</span>
      </div>
      <input
        type="text"
        value={addressDraft}
        onChange={(e) => setAddressDraft(e.target.value)}
        placeholder="e.g. Cummins, Yarm Road, Darlington, DL1 4PW"
        className="w-full text-[12px] border border-neutral-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#F26522] focus:border-[#F26522]"
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleVerifyClick}
          disabled={!addressDraft.trim()}
          className="px-3 py-1.5 text-[12px] bg-[#F26522] text-white rounded-md hover:bg-[#d9561d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Verify location
        </button>
      </div>
    </div>
  );
}
